import React, { createContext, useState, useContext, useEffect } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const skipLogin = process.env.REACT_APP_SKIP_LOGIN === 'true';

    if (skipLogin) {
      // Function to update user role based on URL
      const updateUserRole = () => {
        const currentPath = window.location.pathname;
        const isCoachArea = currentPath.startsWith('/coach');
        const isUserArea = currentPath.startsWith('/user');
        const isAdminArea = currentPath.startsWith('/admin');

        let newRole = 'coach';
        if (isAdminArea) newRole = 'admin';
        else if (isCoachArea) newRole = 'coach';
        else if (isUserArea) newRole = 'client';

        setUser({
          id: 'dev-user',
          name: 'Development User',
          email: 'dev@example.com',
          role: newRole
        });
      };

      // Set initial user
      updateUserRole();

      // Listen for URL changes
      const handleLocationChange = () => {
        updateUserRole();
      };

      window.addEventListener('popstate', handleLocationChange);

      setLoading(false);

      return () => {
        window.removeEventListener('popstate', handleLocationChange);
      };
    }

    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    if (token && userData) {
      setUser(JSON.parse(userData));
    }
    setLoading(false);
  }, []);

  // Update role on programmatic navigation in development mode
  useEffect(() => {
    const skipLogin = process.env.REACT_APP_SKIP_LOGIN === 'true';
    if (skipLogin && user && user.id === 'dev-user') {
      const currentPath = window.location.pathname;
      const isCoachArea = currentPath.startsWith('/coach');
      const isUserArea = currentPath.startsWith('/user');
      const isAdminArea = currentPath.startsWith('/admin');

      let newRole = user.role;
      if (isAdminArea) newRole = 'admin';
      else if (isCoachArea) newRole = 'coach';
      else if (isUserArea) newRole = 'client';

      if (user.role !== newRole) {
        setUser(prev => ({ ...prev, role: newRole }));
      }
    }
  }, [user]);

  const login = async (credentials) => {
    try {
      const response = await authAPI.login(credentials);
      const { token, user } = response.data;

      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      setUser(user);

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Login failed'
      };
    }
  };

  const register = async (userData) => {
    try {
      const response = await authAPI.register(userData);
      const { token, user } = response.data;

      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      setUser(user);

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Registration failed'
      };
    }
  };

  const logout = async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setUser(null);
    }
  };

  const updateUser = (updatedUserData) => {
    const newUser = { ...user, ...updatedUserData };
    setUser(newUser);
    localStorage.setItem('user', JSON.stringify(newUser));
  };

  const value = {
    user,
    login,
    register,
    logout,
    updateUser,
    loading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
